import { addDays, format, subDays } from "date-fns"
import { Cake } from "lucide-react"
import React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import InfoIcon from "@/assets/icons/settings/info-icon.tsx"
import { DrawerDatePicker } from "@/components/generic-date-picker/drawer-date-picker.tsx"
import OverflowMenuItem from "@/components/task-card/overflow-menu-item.tsx"
import { useGetAnimalByIdQuery } from "@/features/farm/api/animals-api.ts"
import { useGetSpeciesQuery } from "@/features/farm/api/species-api.ts"
import AssociatedAnimalsList from "@/features/farm/components/associated-animals-list/associated-animals-list.tsx"
import DrawerAnimals from "@/features/farm/components/drawer-animals/drawer-animals.tsx"
import { type GestationCalendar } from "@/features/gestational-calendar/types/calendar-types.ts"
import DrawerAnimalsAssocTrigger from "@/features/tasks/components/drawer-animals-assoc-trigger/drawer-animals-assoc-trigger.tsx"
import { useAppDispatch } from "@/redux/hooks.ts"
import {
  setGestationalCalendarDraftAnimal,
  setGestationalCalendarDraftInseminationDate,
} from "@/redux/slices/gestational-calendars-draft-slice.ts"

interface GenerateCalendarFormProps {
  gestationCalendarDraft: GestationCalendar
}

const GenerateCalendarForm: React.FC<GenerateCalendarFormProps> = ({
  gestationCalendarDraft,
}) => {
  const dispatch = useAppDispatch()
  const { animalId } = useParams()
  const { t } = useTranslation()
  const { data: species } = useGetSpeciesQuery()
  const { data: selectedAnimal } = useGetAnimalByIdQuery(
    gestationCalendarDraft.animal,
    {
      skip: !gestationCalendarDraft.animal,
    },
  )

  const selectedSpecies =
    species &&
    Object.values(species)?.find(
      (species) => species.id === selectedAnimal?.species,
    )

  const handleDateChange = (insemination_date: string) => {
    dispatch(setGestationalCalendarDraftInseminationDate(insemination_date))
  }

  const handleAnimalsChange = (animalId: string[]) => {
    dispatch(setGestationalCalendarDraftAnimal(animalId[0]))
  }

  const expectedBirtDate = addDays(
    new Date(gestationCalendarDraft.insemination_date),
    selectedSpecies?.gestation_period || 0,
  )

  const selectedAnimalId = gestationCalendarDraft.animal
    ? [gestationCalendarDraft.animal]
    : []

  return (
    <div className={"flex flex-col gap-[10px]"}>
      <div className={"widget-border flex items-center gap-3 bg-white p-4"}>
        <InfoIcon />
        <div className={"text-neutral-700"}>
          <p>
            {t("currentAvailableSpeciesForCalendarAre")}{" "}
            <span className={"text-body-xs font-semibold"}>
              {t("Cows")}, {t("Pork")}
            </span>
          </p>
        </div>
      </div>
      <i className={"ml-2 mt-4 text-body-xs text-neutral-500"}>
        {t("selectDesiredAnimal")}
      </i>
      <OverflowMenuItem isDisabled={!!animalId}>
        <DrawerAnimals
          selectionType="single"
          currentValue={selectedAnimalId}
          onSaveClick={handleAnimalsChange}
          TriggerComponent={DrawerAnimalsAssocTrigger}
        />
        {gestationCalendarDraft.animal && (
          <AssociatedAnimalsList
            animals={Array.of(gestationCalendarDraft.animal)}
            onAnimalsChange={handleAnimalsChange}
          />
        )}
      </OverflowMenuItem>
      <i className={"ml-2 mt-2 text-body-xs text-neutral-500"}>
        {t("inseminationDate")}
      </i>
      <OverflowMenuItem
        isDisabled={!gestationCalendarDraft.animal || !selectedSpecies}
      >
        <DrawerDatePicker
          className={"h-10"}
          onChange={handleDateChange}
          value={gestationCalendarDraft.insemination_date}
          dateInterval={{
            after: subDays(
              new Date(),
              selectedSpecies?.gestation_period || 365,
            ),
            before: addDays(new Date(), 365),
          }}
        />
      </OverflowMenuItem>
      {gestationCalendarDraft.insemination_date &&
        gestationCalendarDraft.animal && (
          <OverflowMenuItem
            className={"flex items-center justify-center gap-5"}
          >
            <div className={"text-base font-medium text-neutral-700"}>
              {t("expectedBirthDate")}
            </div>
            <div
              className={
                "flex w-full flex-row items-center justify-center gap-3"
              }
            >
              <Cake
                strokeWidth={1.5}
                className={
                  "h-[30px] w-[30px] rounded-[5px] bg-amber-600 p-[5px] font-light text-white"
                }
              />
              <p className={"text-body-md font-semibold text-brown-500"}>
                {format(expectedBirtDate, "dd.MM.yyyy")}
              </p>
            </div>
            <i>
              {t("expectedGestationPeriod", {
                days: selectedSpecies?.gestation_period,
              })}
            </i>
          </OverflowMenuItem>
        )}
    </div>
  )
}

export default GenerateCalendarForm
